import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SmartDuplicateDetector from './SmartDuplicateDetector';
import TestCoverageAdvisor from './TestCoverageAdvisor';
import RelatedFeaturesFinder from './RelatedFeaturesFinder';
import './SmartToolsPage.css';

/**
 * SmartToolsPage - Page conteneur pour les 3 Smart Tools
 * 
 * Remplace l'ancien "Embedding Visualizer" (trop technique) par 3 outils
 * intelligents et accessibles au grand public :
 * 
 * 1. 🔍 Smart Duplicate Detector - Détection automatique de doublons
 * 2. 📊 Test Coverage Advisor - Analyse de couverture et recommandations
 * 3. 🔗 Related Features Finder - Découverte de features liées
 * 
 * Tous utilisent les embeddings vectoriels en arrière-plan sans exposer
 * la complexité technique à l'utilisateur.
 */
const SmartToolsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('duplicates'); // duplicates, coverage, related

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const handleNavigateToTestGenerator = (nodeId, nodeName) => {
    // Naviguer vers FeatureManager avec les paramètres pour ouvrir la modal TestGeneration
    navigate('/feature-manager', { 
      state: { 
        openTestGeneration: true, 
        selectedNodeId: nodeId,
        selectedNodeName: nodeName
      } 
    });
  };

  return (
    <div className="smart-tools-page">
      <div className="page-header">
        <h1>🧠 {t('smartTools.pageTitle')}</h1>
        <p className="page-description">{t('smartTools.pageDescription')}</p>
      </div>

      <div className="tools-tabs">
        <button
          className={`tab-button ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => setActiveTab('duplicates')}
        >
          <span className="tab-icon">🔍</span>
          <span className="tab-label">{t('smartTools.tabs.duplicates')}</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'coverage' ? 'active' : ''}`}
          onClick={() => setActiveTab('coverage')}
        >
          <span className="tab-icon">📊</span>
          <span className="tab-label">{t('smartTools.tabs.coverage')}</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'related' ? 'active' : ''}`}
          onClick={() => setActiveTab('related')}
        >
          <span className="tab-icon">🔗</span>
          <span className="tab-label">{t('smartTools.tabs.related')}</span>
        </button>
      </div>

      <div className="tools-content">
        {activeTab === 'duplicates' && <SmartDuplicateDetector />}
        {activeTab === 'coverage' && <TestCoverageAdvisor onNavigateToTestGenerator={handleNavigateToTestGenerator} />}
        {activeTab === 'related' && <RelatedFeaturesFinder />}
      </div>

      <div className="tools-footer">
        <p className="footer-info">
          💡 {t('smartTools.footerInfo')}
        </p>
      </div>
    </div>
  );
};

export default SmartToolsPage;
